import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApiResponse,
  ICreateUser,
  IDeleteUser,
  ILimit,
  IUser,
} from '../sagas/userSaga';

export interface UserState {
  users: ApiResponse;
  loading: boolean;
  error: boolean;
  user: IUser;
  shouldRefetchUsers: boolean;
}

export const initialState: UserState = {
  users: {
    data: [],
    pagination: {
      totalRecords: 0,
      totalItems: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
    success: false,
    message: '',
  },
  loading: true,
  error: false,
  user: {
    userId: '',
    firstName: '',
    lastName: '',
    authUserId: '',
    email: '',
    roleId: '',
    createdOn: '',
    updatedOn: '',
  },
  shouldRefetchUsers: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUsers: (state, action: PayloadAction<ILimit>) => {
      state.loading = true;
    },
    getUsersSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.users = { ...action.payload };
    },
    getUsersFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    getUser: (state) => {
      state.loading = true;
    },
    getUserSuccess: (state, action: PayloadAction<IUser>) => {
      state.loading = false;
      state.user = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    getUserFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    createUser: (state, action: PayloadAction<ICreateUser>) => {
      state.loading = true;
    },
    createUserSuccess: (state, action: PayloadAction<IUser>) => {
      state.loading = false;
      state.user = { ...action.payload }; // Avoid direct assignment, create a new object
      state.shouldRefetchUsers = true;
    },
    createUserFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    updateUser: (state, action: PayloadAction<ICreateUser>) => {
      state.loading = true;
    },
    updateUserSuccess: (state, action: PayloadAction<IUser>) => {
      state.loading = false;
      state.user = { ...action.payload };
      state.shouldRefetchUsers = true;
    },
    updateUserFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    deleteUser: (state, action: PayloadAction<IDeleteUser>) => {
      state.loading = true;
    },
    deleteUserSuccess: (state, action: PayloadAction<IUser>) => {
      state.loading = false;
      state = {
        ...state,
        users: {
          ...state.users,
          data: state.users.data.filter(
            (item) => item.userId !== action.payload.userId,
          ),
        },
      };
      state.shouldRefetchUsers = true;
    },
    deleteUserFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    setShouldRefetchUsers: (state, action: PayloadAction<boolean>) => {
      state.shouldRefetchUsers = action.payload;
    },
  },
});

const userReducer = userSlice.reducer;
export const {
  getUsers,
  getUsersSuccess,
  getUsersFailure,
  getUser,
  getUserSuccess,
  getUserFailure,
  createUser,
  createUserSuccess,
  createUserFailure,
  updateUser,
  updateUserFailure,
  updateUserSuccess,
  deleteUser,
  deleteUserSuccess,
  deleteUserFailure,
  setShouldRefetchUsers,
} = userSlice.actions;
export default userReducer;
